import { isRef, toRaw } from 'vue';
import { BOOT_TEST_GLOBALS } from '@/shared/constants/boot-test-globals';

const LANGUAGE_SUBTAG = /^[A-Za-z]{2,3}$/;
const SCRIPT_SUBTAG = /^[A-Za-z]{4}$/;
const REGION_SUBTAG = /^([A-Za-z]{2}|[0-9]{3})$/;

export type VuetifyLocaleBinding = {
  current: { value: string };
  fallback?: { value?: string };
  messages?: { value?: Record<string, unknown> };
};

let vuetifyLocaleBinding: VuetifyLocaleBinding | null = null;

function rawLocaleBinding(binding: VuetifyLocaleBinding): VuetifyLocaleBinding {
  return toRaw(binding);
}

function writeVuetifyCurrent(binding: VuetifyLocaleBinding, key: string): void {
  const current = rawLocaleBinding(binding).current;

  if (isRef(current) || (current && typeof current === 'object' && 'value' in current)) {
    current.value = key;
  }
}

function normalizeLanguage(part: string): string {
  return part.toLowerCase();
}

function normalizeScript(part: string): string {
  return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
}

function normalizeRegion(part: string): string {
  return /^[0-9]{3}$/.test(part) ? part : part.toUpperCase();
}

function camelCaseLocaleParts(parts: string[]): string {
  return (
    parts[0].toLowerCase() +
    parts
      .slice(1)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join('')
  );
}

function readBindingString(refLike: { value?: string } | undefined): string | undefined {
  if (!refLike || typeof refLike !== 'object' || !('value' in refLike)) {
    return undefined;
  }

  const value = refLike.value;
  return typeof value === 'string' && value ? value : undefined;
}

/**
 * Normalize a locale string to a BCP 47 tag (`sv`, `en-GB`, `zh-Hans`, `es-419`).
 * Returns null for empty or structurally invalid values so callers can leave
 * the previous document language in place.
 */
export function normalizeBcp47Locale(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parts = trimmed.split('-');

  if (parts.length < 1 || parts.length > 3 || !LANGUAGE_SUBTAG.test(parts[0])) {
    return null;
  }

  if (parts.length === 1) {
    return normalizeLanguage(parts[0]);
  }

  if (parts.length === 2) {
    if (SCRIPT_SUBTAG.test(parts[1])) {
      return `${normalizeLanguage(parts[0])}-${normalizeScript(parts[1])}`;
    }

    if (REGION_SUBTAG.test(parts[1])) {
      return `${normalizeLanguage(parts[0])}-${normalizeRegion(parts[1])}`;
    }

    return null;
  }

  if (!SCRIPT_SUBTAG.test(parts[1]) || !REGION_SUBTAG.test(parts[2])) {
    return null;
  }

  return `${normalizeLanguage(parts[0])}-${normalizeScript(parts[1])}-${normalizeRegion(parts[2])}`;
}

type LangRoot = { setAttribute: (name: string, value: string) => void } | null;

function defaultLangRoot(): LangRoot {
  return typeof document !== 'undefined' ? document.documentElement : null;
}

export function applyDocumentLang(tag: string, root: LangRoot = defaultLangRoot()): void {
  root?.setAttribute('lang', tag);
}

function vuetifyLocaleKeyCandidates(tag: string): string[] {
  const parts = tag.split('-');
  const keys = [tag, camelCaseLocaleParts(parts)];

  if (parts.length >= 3) {
    keys.push(`${parts[0]}-${parts[1]}`, camelCaseLocaleParts(parts.slice(0, 2)));
  }

  keys.push(parts[0]);
  return [...new Set(keys)];
}

export function resolveVuetifyLocaleKey(tag: string, binding: VuetifyLocaleBinding): string {
  const raw = rawLocaleBinding(binding);
  const primary = tag.split('-')[0];
  const fallback = readBindingString(raw.fallback) ?? primary;
  const messages = raw.messages?.value;

  if (!messages) {
    return fallback;
  }

  for (const key of vuetifyLocaleKeyCandidates(tag)) {
    if (Object.hasOwn(messages, key)) {
      return key;
    }
  }

  return fallback;
}

/**
 * Register the Vuetify locale instance so `hSetLocale` can keep it aligned.
 * Pass null to unbind (tests).
 */
export function bindVuetifyLocale(binding: VuetifyLocaleBinding | null): void {
  vuetifyLocaleBinding = binding;
}

export function applyVuetifyLocale(tag: string): void {
  if (!vuetifyLocaleBinding) {
    return;
  }

  const binding = rawLocaleBinding(vuetifyLocaleBinding);
  writeVuetifyCurrent(binding, resolveVuetifyLocaleKey(tag, binding));
}

function readVuetifyLocaleKey(tag: string): string {
  return vuetifyLocaleBinding ? resolveVuetifyLocaleKey(tag, rawLocaleBinding(vuetifyLocaleBinding)) : tag.split('-')[0];
}

/**
 * Keep the Playwright probe in sync when tests expose a writable window key.
 * Skip a getter-only property so `src/main.js` can expose the live Vuetify ref.
 */
function syncTestVuetifyLocale(tag: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const globals = window as unknown as Record<string, unknown>;
  const windowKey = BOOT_TEST_GLOBALS.HELIX_VUETIFY_LOCALE;
  const descriptor = Object.getOwnPropertyDescriptor(globals, windowKey);

  if (descriptor?.get && !descriptor.set) {
    return;
  }

  if (!Object.hasOwn(globals, windowKey)) {
    return;
  }

  globals[windowKey] = readVuetifyLocaleKey(tag);
}

/**
 * Write a valid locale to `<html lang>` and the bound Vuetify locale.
 * Invalid values are ignored so the last valid language stays in place.
 */
export function applyLocaleToDocument(value: unknown): string | null {
  const tag = normalizeBcp47Locale(value);

  if (!tag) {
    return null;
  }

  applyDocumentLang(tag);
  applyVuetifyLocale(tag);
  syncTestVuetifyLocale(tag);

  return tag;
}
